import type { AttachUploadLinkHandlers } from './viewer/tools/files/editorjs-attach-upload-link-tool';
import type { GalleryEditRequest } from './viewer/tools/gallery/editorjs-gallery-tool';
import type { AiInstructionsEditRequest } from './viewer/tunes/editorjs-ai-instructions-tune';

import { useApolloClient } from '@apollo/client/react';
import { useCallback, useRef, useState } from 'react';

import { GET_UPLOADS_FOR_DOCUMENT } from '#graphql/queries/document-uploads';
import { DocumentAttachFileModal } from '#pages/document/files/document-attach-file-modal';
import {
  composerAttachmentMarkdownHref,
  type UploadFileResult,
} from '#pages/document/upload-image';

import { useDocumentChatComposer } from '../document-chat-composer-context';
import { useDocumentContext } from '../document-context';

import { useEditDocument } from './edit-document-context';
import { RenameDocumentModal } from './modals/rename-document-modal';
import { useRenameDocument } from './use-rename-document';
import { EditDocumentEditor } from './viewer/editor';
import { AiInstructionsModal } from './viewer/modals/ai-instructions-modal';
import { GalleryEditModal } from './viewer/tools/gallery/gallery-edit-modal';
import { GalleryImageModalProvider } from './viewer/tools/gallery/gallery-image-modal-context';

interface ViewDocumentViewerProps {
  apiHost: string;
  userEmail: string;
  onCreateSubpage: () => void;
  /** Navigate to a document path (client-side). Used by Subpages block links. */
  onNavigateToDocument?: (path: string) => void;
  isRenameOpen: boolean;
  onRenameOpenChange: (open: boolean) => void;
}

export function ViewDocumentViewer({
  apiHost,
  userEmail,
  onCreateSubpage,
  onNavigateToDocument,
  isRenameOpen,
  onRenameOpenChange,
}: ViewDocumentViewerProps) {
  const {
    documentId,
    isNewDocument,
    editorPlaceholderMuted,
    path,
    displayPath,
    editorInitialData,
    childrenItems,
  } = useDocumentContext();
  const { editorRef } = useEditDocument();
  const { documentPathForRename, getSerializedDocument, handleRename } =
    useRenameDocument();

  const openRename = useCallback(() => {
    onRenameOpenChange(true);
  }, [onRenameOpenChange]);

  const { appendEmphasis, focusComposer } = useDocumentChatComposer();
  const apolloClient = useApolloClient();
  const attachLinkSessionRef = useRef<AttachUploadLinkHandlers | null>(null);
  const attachUploadSucceededRef = useRef(false);
  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const [galleryEditRequest, setGalleryEditRequest] =
    useState<GalleryEditRequest | null>(null);
  const [aiInstructionsRequest, setAiInstructionsRequest] =
    useState<AiInstructionsEditRequest | null>(null);

  const handleRequestGalleryEdit = useCallback(
    (request: GalleryEditRequest) => {
      setGalleryEditRequest(request);
    },
    [],
  );

  const handleRequestAiInstructionsEdit = useCallback(
    (request: AiInstructionsEditRequest) => {
      setAiInstructionsRequest(request);
    },
    [],
  );

  const handleAddToChat = useCallback(
    (blockId: string) => {
      appendEmphasis(blockId);
      focusComposer();
    },
    [appendEmphasis, focusComposer],
  );

  const handleUpload = useCallback(
    async (file: File): Promise<string> => {
      const { uploadImageFile } = await import('../upload-image');
      const result = await uploadImageFile(file, {
        apiHost,
        userEmail,
        ...(documentId ? { documentId } : {}),
      });
      return result.url;
    },
    [apiHost, documentId, userEmail],
  );

  const handleRequestAttachUploadLink = useCallback(
    (handlers: AttachUploadLinkHandlers) => {
      attachUploadSucceededRef.current = false;
      attachLinkSessionRef.current = handlers;
      setAttachModalOpen(true);
    },
    [],
  );

  const handleAttachModalOpenChange = useCallback((open: boolean) => {
    if (!open) {
      if (!attachUploadSucceededRef.current) {
        attachLinkSessionRef.current?.cancel();
      }

      attachUploadSucceededRef.current = false;
      attachLinkSessionRef.current = null;
      setAttachModalOpen(false);
    }
  }, []);

  const handleAttachUploadResult = useCallback(
    (result: UploadFileResult) => {
      const href = composerAttachmentMarkdownHref(
        result.id,
        result.url,
        result.isEncrypted,
        apiHost,
      );
      attachUploadSucceededRef.current = true;
      attachLinkSessionRef.current?.applyHref(href);
      attachLinkSessionRef.current = null;
      setAttachModalOpen(false);
    },
    [apiHost],
  );

  const handleAttachUploadedRefetch = useCallback(() => {
    if (!documentId) return;

    void apolloClient.refetchQueries({ include: [GET_UPLOADS_FOR_DOCUMENT] });
  }, [apolloClient, documentId]);

  return (
    <GalleryImageModalProvider>
      <GalleryEditModal
        request={galleryEditRequest}
        onDismiss={() => setGalleryEditRequest(null)}
        onUpload={handleUpload}
      />
      <AiInstructionsModal
        request={aiInstructionsRequest}
        onDismiss={() => setAiInstructionsRequest(null)}
      />
      <DocumentAttachFileModal
        open={attachModalOpen}
        onOpenChange={handleAttachModalOpenChange}
        apiHost={apiHost}
        userEmail={userEmail}
        documentId={documentId}
        onUploaded={handleAttachUploadedRefetch}
        onUploadResult={handleAttachUploadResult}
      />
      <EditDocumentEditor
        key={documentId ?? path}
        ref={editorRef}
        value={editorInitialData}
        documentId={documentId}
        apiHost={apiHost}
        userEmail={userEmail}
        isNewDocument={isNewDocument}
        editorPlaceholderMuted={editorPlaceholderMuted}
        onUpload={handleUpload}
        parentPath={displayPath}
        children={childrenItems}
        onCreateSubpage={onCreateSubpage}
        onNavigateToDocument={onNavigateToDocument}
        onAddToChat={handleAddToChat}
        onRequestGalleryEdit={handleRequestGalleryEdit}
        onRequestAiInstructionsEdit={handleRequestAiInstructionsEdit}
        onRequestRename={openRename}
        onRequestAttachUploadLink={handleRequestAttachUploadLink}
      />

      {!isNewDocument && (
        <RenameDocumentModal
          open={isRenameOpen}
          onOpenChange={onRenameOpenChange}
          documentPathForRename={documentPathForRename}
          getCurrentValue={getSerializedDocument}
          onRename={handleRename}
        />
      )}
    </GalleryImageModalProvider>
  );
}
